import React from 'react';
import { formatNumbers, formatWithSign, getBetMatchupLabel, getBetPrimaryName, getLiveLabel, getPendingMarketSubLabel, isFreePlayBet, isPropsBet } from '../../../helpers/functions.tsx';
import { BetDetails } from '../../../models/MyBetModel.tsx';
import { MlbPitchersDetailRow } from '../../shared/MlbPitchersDetailRow.tsx';
import BetLogo from '../../player/modals/BetLogo.tsx';

const isMoneylineMarket = (marketName?: string | null) =>
    String(marketName ?? "").toLowerCase() === "moneyline";

const formatWeeklyOdds = (bet: {
    marketName?: string;
    odd?: { price?: number; points?: number | null; selection_line?: string | null };
}) => {
    if (isMoneylineMarket(bet.marketName)) {
        return `ML ${formatWithSign(bet.odd?.price)}`;
    }
    const points = bet.odd?.points != null
        ? `${formatWithSign(bet.odd.points, bet.odd?.selection_line, "points")} `
        : "";
    return `${points}${formatWithSign(bet.odd?.price)}`;
};

interface IBet {
    details: any
    isParlay: any
    togglePlayerDetails: any
    getResult: any
    expandedPlayers: any
    formatDateTime: any
    isProps: any
    amountMode?: 'result' | 'risk'
}

const BetsDetails = (props: IBet) => {
    const { details, isParlay, togglePlayerDetails, getResult, expandedPlayers, formatDateTime, isProps, amountMode = 'result' } = props;

    const renderAmount = (player: BetDetails, value: number) => {
        if (amountMode === 'risk') {
            return (
                <div className="selection-money">
                    ${formatNumbers(player.bet ?? 0)}
                </div>
            );
        }

        return (
            <div className={`selection-money ${value < 0 ? 'negative black-letters' : value > 0 ? 'positive black-letters' : ''}`}>
                {value > 0 && '+ $'}
                {value < 0 && '- $'}
                {value === 0 && '$'}
                {formatNumbers(Math.abs(value))}
            </div>
        );
    };
    return (
        <>
            {details.map((player: BetDetails, index: any) => {
                const uniqueKey = `${player.betDate}-${index}`;
                const rowBg = index % 2 === 0 ? "#e6e6e6" : "#ffffff";
                const value = getResult(player);
                const liveLabel = getLiveLabel(player.gameType);
                if (isParlay(player)) {
                    return (
                        <React.Fragment key={uniqueKey}>
                            <tr onClick={() => togglePlayerDetails(player.ticket)} style={{ cursor: "pointer", backgroundColor: rowBg }}>
                                <td colSpan={8}>
                                    <div className="bet-selection-container">
                                        <div className="d-flex align-items-center">
                                            <span className='description-text'>{player.description}</span>
                                        </div>
                                        {renderAmount(player, value)}
                                    </div>
                                </td>
                            </tr>

                            {!expandedPlayers.has(player.ticket) && (
                                <tr style={{ backgroundColor: rowBg }}>
                                    <td colSpan={8}>
                                        <div className="selection-item">
                                            <div className="selections-container">
                                                {player.selections?.map((selection: any, idx: any) => (
                                                    <div key={idx} className="selection-item-details">
                                                        <BetLogo bet={selection} className="sport-logos-pending" alt="logo" />
                                                        <span className="fw-bold inter body-letters">
                                                            {getBetPrimaryName(selection)}
                                                        </span>
                                                        <span className="bet-format">
                                                            {formatWeeklyOdds(selection)}
                                                        </span>
                                                        &nbsp;
                                                        {getPendingMarketSubLabel(selection) && (
                                                            <span className="name-pending-props">
                                                                {getPendingMarketSubLabel(selection)}
                                                            </span>
                                                        )}
                                                        &nbsp;
                                                        <span
                                                            style={{
                                                                color: selection.grader?.result === "Won" ? "green" :
                                                                    selection.grader?.result === "Lost" ? "red" : "black",
                                                                fontWeight: "bold",
                                                            }}>
                                                            {selection.grader?.result === "Won" ? "W" :
                                                                selection.grader?.result === "Lost" ? "L" : ""}
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </td>
                                </tr>
                            )}

                            {expandedPlayers.has(player.ticket) && (
                                <>
                                    <tr>
                                        <td colSpan={8}>
                                            <div className="selections-container-detail">
                                                <table>
                                                    <tbody>
                                                        <tr>
                                                            <th className="title-table-pending">Ticket#</th>
                                                            <td className="body-table-pending">{player.ticket}</td>
                                                        </tr>
                                                        {/* <tr>
                                                            <th className="title-table-pending">Status</th>
                                                            <td className="body-table-pending">{capitalize(player.betStatus)}</td>
                                                        </tr> */}
                                                        <tr>
                                                            <th className="title-table-pending">Risk/Win</th>
                                                            <td className="body-table-pending">${formatNumbers(player.bet)} / ${formatNumbers(player.reward)}</td>
                                                        </tr>
                                                        <tr>
                                                            <th className="title-table-pending">Accepted (PST)</th>
                                                            <td className="body-table-pending">{formatDateTime(player.betDate)}</td>
                                                        </tr>
                                                    </tbody>
                                                </table>
                                            </div>
                                        </td>
                                    </tr>

                                    {player.selections?.map((selection: any, idx: any) => (
                                        <tr key={idx}>
                                            <td colSpan={8} className="detail">
                                                <div style={{ display: "flex", flexDirection: "column", gap: "10px" }} className='mt-2'>
                                                    <div style={{ display: "flex", alignItems: "center", gap: "10px" }} >
                                                        <BetLogo bet={selection} className="sport-logos-pending" alt="logo" />
                                                        <span className="body-letters">{getBetPrimaryName(selection)}</span>
                                                        <span className='bet-format'>
                                                            {formatWeeklyOdds(selection)}
                                                        </span>
                                                        {getPendingMarketSubLabel(selection) && (
                                                            <span className="name-pending-props">
                                                                {getPendingMarketSubLabel(selection)}
                                                            </span>
                                                        )}

                                                    </div>
                                                    <table>
                                                        <tbody>
                                                            <tr>
                                                                <th className="title-table-pending">Ticket#</th>
                                                                <td className="body-table-pending">{selection.ticket}</td>
                                                            </tr>
                                                            <tr>
                                                                <th className="title-table-pending">Status</th>
                                                                <td className="body-table-pending">
                                                                    {selection.grader?.result}
                                                                </td>
                                                            </tr>
                                                            <tr>
                                                                <th className="title-table-pending">Score</th>
                                                                <td className="body-table-pending">
                                                                    <span>{selection.grader?.away_team_display} - score: {selection.grader?.away_score} </span>/ <span>{selection.grader?.home_team_display} - score:  {selection.grader?.home_score}</span>
                                                                </td>
                                                            </tr>
                                                            <tr>
                                                                <th className="title-table-pending">Game</th>
                                                                <td className="body-table-pending">{getBetMatchupLabel(selection)}</td>
                                                            </tr>
                                                            <tr>
                                                                <th className="title-table-pending">Sport</th>
                                                                <td className="body-table-pending">{selection.sport}</td>
                                                            </tr>
                                                            <tr>
                                                                <th className="title-table-pending">Selection</th>
                                                                <td className="body-table-pending">{selection.marketName}</td>
                                                            </tr>
                                                            <MlbPitchersDetailRow pitchers={selection.pitchers} />
                                                            <tr>
                                                                <th className="title-table-pending">Scheduled (PST)</th>
                                                                <td className="body-table-pending">{formatDateTime(selection.gameDate)}</td>
                                                            </tr>
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </>
                            )}
                        </React.Fragment>
                    );
                }
                if (isProps(player) || Boolean(getPendingMarketSubLabel(player))) {
                    return (
                        <React.Fragment key={uniqueKey}>
                            <tr onClick={() => player.ticket ? togglePlayerDetails(player.ticket) : null} style={{ cursor: "pointer", backgroundColor: rowBg }}>
                                <td colSpan={8}>
                                    <div className="bet-selection-container">
                                        <div className="d-flex align-items-center">
                                            <span className="body-letters black-letters body-letters-hanging">
                                                <BetLogo
                                                    bet={player}
                                                    className={` ${isPropsBet(player) ? "sport-logo-pending-props" : "sport-logos-pending"}`}
                                                    alt="logo"
                                                />

                                                <span className="fw-bold inter">
                                                    {getBetPrimaryName(player)}
                                                </span>
                                                {player.gameType && (
                                                    <span className="fw-bold inter">
                                                        {liveLabel && ` (${liveLabel})`}
                                                    </span>
                                                )}

                                                <span className='bet-format'>
                                                    {formatWeeklyOdds(player)}
                                                </span>&nbsp;

                                                <span
                                                    style={{
                                                        color: player.grader?.result === "Won" ? "green" :
                                                            player.grader?.result === "Lost" ? "red" : "black",
                                                        fontWeight: "bold",
                                                    }}>
                                                    {player.grader?.result === "Won" ? "W" :
                                                        player.grader?.result === "Lost" ? "L" : ""}
                                                </span>
                                                &nbsp;
                                                {isFreePlayBet(player) && (
                                                    <span className='bet-free'>{player.freeBet}</span>
                                                )}
                                                {getPendingMarketSubLabel(player) && (
                                                    <span className='name-pending-props d-block'>
                                                        {getPendingMarketSubLabel(player)}
                                                    </span>
                                                )}
                                            </span>
                                        </div>
                                        {renderAmount(player, getResult(player))}
                                    </div>
                                </td>
                            </tr>

                            {expandedPlayers.has(player.ticket) && (
                                <tr>
                                    <td colSpan={8} className="detail">
                                        <table style={{ margin: "0 auto" }}>
                                            <tbody>
                                                <tr>
                                                    <th className="title-table-pending">Ticket#</th>
                                                    <td className="body-table-pending">{player.ticket}</td>
                                                </tr>
                                                <tr>
                                                    <th className="title-table-pending">Status</th>
                                                    <td className="body-table-pending">
                                                        {player.grader?.result}
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <th className="title-table-pending">Score</th>
                                                    <td className="body-table-pending">
                                                        <span>{player.grader?.away_team_display} - score: {player.grader?.away_score} </span>/ <span>{player.grader?.home_team_display} - score:  {player.grader?.home_score}</span>
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <th className="title-table-pending">Risk/Win</th>
                                                    <td className="body-table-pending">${formatNumbers(player.bet)} / ${formatNumbers(player.reward)}</td>
                                                </tr>
                                                <tr>
                                                    <th className="title-table-pending">Game</th>
                                                    <td className="body-table-pending">
                                                        {player.odd?.player_id
                                                            ? player.odd?.market
                                                            : getBetMatchupLabel(player)
                                                        }
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <th className="title-table-pending">Sport</th>
                                                    <td className="body-table-pending">{player.sport}</td>
                                                </tr>
                                                <tr>
                                                    <th className="title-table-pending">Selection</th>
                                                    <td className="body-table-pending">{getPendingMarketSubLabel(player) || `${player.marketName} - ${player.betName}`}</td>
                                                </tr>
                                                <tr>
                                                    <th className="title-table-pending">Scheduled (PST)</th>
                                                    <td className="body-table-pending">{formatDateTime(player.gameDate)}</td>
                                                </tr>
                                                <tr>
                                                    <th className="title-table-pending">Accepted (PST)</th>
                                                    <td className="body-table-pending">{formatDateTime(player.betDate)}</td>
                                                </tr>
                                                <tr>
                                                    <th className="title-table-pending">Latest Update (PST)</th>
                                                    <td className="body-table-pending">{formatDateTime(player.lastUpdateBySystem)}</td>
                                                </tr>

                                            </tbody>
                                        </table>
                                    </td>
                                </tr>
                            )}
                        </React.Fragment>
                    );
                }
                return (
                    <React.Fragment key={uniqueKey}>
                        <tr onClick={() => player.ticket ? togglePlayerDetails(player.ticket) : null} style={{ cursor: "pointer", backgroundColor: rowBg }}>
                            <td colSpan={8}>
                                <div className="bet-selection-container">
                                    <div className="d-flex align-items-center">
                                        <span className="body-letters black-letters body-letters-hanging">
                                            <BetLogo
                                                bet={player}
                                                className={` ${isPropsBet(player) ? "sport-logo-pending-props" : "sport-logos-pending"}`}
                                                alt="logo"
                                            />

                                            <span className="fw-bold inter">
                                                {getBetPrimaryName(player)}
                                            </span>

                                            <span className='bet-format'>
                                                {formatWeeklyOdds(player)}
                                            </span>
                                            &nbsp;
                                            {player.gameType && (
                                                <span className="fw-bold inter">
                                                    ({player?.gameType})
                                                </span>
                                            )}&nbsp;

                                            <span
                                                style={{
                                                    color: player.grader?.result === "Won" ? "green" :
                                                        player.grader?.result === "Lost" ? "red" : "black",
                                                    fontWeight: "bold",
                                                }}>
                                                {player.grader?.result === "Won" ? "W" :
                                                    player.grader?.result === "Lost" ? "L" : ""}
                                            </span>
                                            &nbsp;
                                            {isFreePlayBet(player) && (
                                                <span className='bet-free'>{player.freeBet}</span>
                                            )}
                                            {getPendingMarketSubLabel(player) && (
                                                <span className='name-pending-props d-block'>
                                                    {getPendingMarketSubLabel(player)}
                                                </span>
                                            )}
                                        </span>
                                        </div>
                                        {renderAmount(player, getResult(player))}
                                    </div>
                                </td>
                            </tr>

                        {expandedPlayers.has(player.ticket) && (
                            <tr>
                                <td colSpan={8} className="detail">
                                    <table style={{ margin: "0 auto" }}>
                                        <tbody>
                                            <tr>
                                                <th className="title-table-pending">Ticket#</th>
                                                <td className="body-table-pending">{player.ticket}</td>
                                            </tr>
                                            <tr>
                                                <th className="title-table-pending">Status</th>
                                                <td className="body-table-pending">
                                                    {player.grader?.result}
                                                </td>
                                            </tr>
                                            <tr>
                                                <th className="title-table-pending">Score</th>
                                                <td className="body-table-pending">
                                                    <span>{player.grader?.away_team_display} - score: {player.grader?.away_score} </span>/ <span>{player.grader?.home_team_display} - score:  {player.grader?.home_score}</span>
                                                </td>
                                            </tr>
                                            <tr>
                                                <th className="title-table-pending">Risk/Win</th>
                                                <td className="body-table-pending">${formatNumbers(player.bet)} / ${formatNumbers(player.reward)}</td>
                                            </tr>
                                            <tr>
                                                <th className="title-table-pending">Game</th>
                                                <td className="body-table-pending">
                                                    {player.odd?.player_id
                                                        ? player.odd?.market
                                                        : <>{player.odd?.market ? `${player.odd.market}, ` : ''}{getBetMatchupLabel(player)}</>
                                                    }
                                                </td>
                                            </tr>
                                            <tr>
                                                <th className="title-table-pending">Sport</th>
                                                <td className="body-table-pending">{player.sport}</td>
                                            </tr>
                                            <tr>
                                                <th className="title-table-pending">Selection</th>
                                                <td className="body-table-pending">{getPendingMarketSubLabel(player) || `${player.gameType} - ${player.odd?.market} - ${player.betName}`}</td>
                                            </tr>
                                            <MlbPitchersDetailRow pitchers={player.pitchers} />
                                            <tr>
                                                <th className="title-table-pending">Scheduled (PST)</th>
                                                <td className="body-table-pending">{formatDateTime(player.gameDate)}</td>
                                            </tr>
                                            <tr>
                                                <th className="title-table-pending">Accepted (PST)</th>
                                                <td className="body-table-pending">{formatDateTime(player.betDate)}</td>
                                            </tr>
                                            <tr>
                                                <th className="title-table-pending">Latest Update (PST)</th>
                                                <td className="body-table-pending">{formatDateTime(player.lastUpdateBySystem)}</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </td>
                            </tr>
                        )}
                    </React.Fragment>
                );
            })}
        </>
    )
}


export default BetsDetails;